import {
  ExpenseRequestControls,
  RejectRequestModal,
  RequestAccessController,
  TravelRequestControls,
  WithdrawRequestModal,
  useRequestDependencies,
} from "entities/Request";
import { memo, useEffect, useState } from "react";
import { Card, Stack } from "react-bootstrap";
import { toast } from "react-toastify";
import "./RequestControlsContainer.styles.css";
import { REQUEST_TYPES, ROLES } from "shared/config";
import { getCostLineDecisions, useCostLinesStore } from "entities/CostLines";
import UploadToOCRModal from "features/OCR/ui/UploadToOCRModal/UploadToOCRModal";
import { useBreakpoints } from "shared/hooks";

const access = new RequestAccessController();

function RequestControlsContainer({
  userRole,
  requestType,
  triggerRequestAccept,
  handleRequestReject,
  triggerRequestSave,
  triggerRequestSubmit,
  triggerRequestReturn,
  loadingState,
  requestStatus,
  maxCostLinesCount,
  updateLoadingState,
  requestCostLines,
  requestId,
}) {
  const costLines = useCostLinesStore((state) => state.costLines);
  const addCostLine = useCostLinesStore((state) => state.addCostLine);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const handleRejectionModalClose = () => setShowRejectionModal(false);
  const handleRejectionModalShow = () => setShowRejectionModal(true);
  const [showOCRUploadModal, setDhowOCRUploadModal] = useState(false);
  const handleOCRUploadModalClose = () => setDhowOCRUploadModal(false);
  const handleOCRUploadModalShow = () => setDhowOCRUploadModal(true);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const handleWithdrawalModalClose = () => setShowWithdrawalModal(false);
  const handleWithdrawalModalShow = () => setShowWithdrawalModal(true);

  const { data: { dependencies } = {} } = useRequestDependencies(
    "travel_line_form",
    requestId,
    {
      triggerRequest: access.to("TravelLine", "edit").provided(),
    }
  );

  const handleCostLineCreation = (e) => {
    e.target.blur();
    if (costLines.length + 1 <= maxCostLinesCount) {
      addCostLine();
    } else {
      toast.warn(
        `You can't create more than ${maxCostLinesCount} cost lines per one request.`,
        { theme: "colored", toastId: "costlines-limit" }
      );
    }
  };

  const handleCostLineCreationWithOCR = () => {
    handleOCRUploadModalShow();
  };

  const [isReadyToBeAccepted, setIsReadyToBeAccepted] = useState(false);
  const [isReadyToBeReturned, setIsReadyToBeReturned] = useState(false);

  useEffect(() => {
    const allDecisionsMade = requestCostLines.every(
      (costLine) =>
        getCostLineDecisions(costLine, userRole).isAccepted ||
        getCostLineDecisions(costLine, userRole).isRejected
    );
    const someRejectedByUserRole = requestCostLines.some((costLine) => {
      const acceptorRole = costLine?.acceptations?.data?.acceptor_role;

      return (
        getCostLineDecisions(costLine, userRole).isRejected &&
        acceptorRole === userRole
      );
    });

    const allAccepted =
      requestCostLines.length === 0 ||
      requestCostLines.every(
        (costLine) => getCostLineDecisions(costLine, userRole).isAccepted
      );

    const someRejected =
      requestCostLines.length > 0 &&
      requestCostLines.some(
        (costLine) => getCostLineDecisions(costLine, userRole).isRejected
      );

    if (userRole === ROLES.ACCOUNTS_PAYABLE) {
      setIsReadyToBeAccepted(allAccepted);
    } else if (userRole === ROLES.COST_GROUP) {
      setIsReadyToBeAccepted(allDecisionsMade);
    } else {
      setIsReadyToBeAccepted(!someRejected);
    }

    setIsReadyToBeReturned(
      allDecisionsMade && !allAccepted && someRejectedByUserRole
    );
  }, [requestCostLines, requestStatus]);

  // TODO Refactor and unification

  const {
    dimension: [, height],
    isMobileDevice,
  } = useBreakpoints();

  return (
    <Card
      as={Stack}
      direction="horizontal"
      className={`request-controls-card ${
        height < 490 && isMobileDevice ? "hide-controls-card" : ""
      }`}
    >
      <WithdrawRequestModal
        updateLoadingState={updateLoadingState}
        show={showWithdrawalModal}
        handleClose={handleWithdrawalModalClose}
        isWithdrawing={loadingState.isWithdrawing}
      />
      <RejectRequestModal
        show={showRejectionModal}
        handleClose={handleRejectionModalClose}
        rejectRequest={handleRequestReject}
        isRejecting={loadingState.isRejecting}
      />
      <UploadToOCRModal
        show={showOCRUploadModal}
        handleClose={handleOCRUploadModalClose}
      />
      {requestType === REQUEST_TYPES.TRAVEL ? (
        <TravelRequestControls
          userRole={userRole}
          loadingState={loadingState}
          requestStatus={requestStatus}
          isReadyToBeAccepted={isReadyToBeAccepted}
          isReadyToBeReturned={isReadyToBeReturned}
          handleCostLineCreation={handleCostLineCreation}
          handleCostLineCreationWithOCR={handleCostLineCreationWithOCR}
          handleWithdrawalModalShow={handleWithdrawalModalShow}
          triggerRequestSave={triggerRequestSave}
          triggerRequestSubmit={triggerRequestSubmit}
          triggerRequestReturn={triggerRequestReturn}
          handleRejectionModalShow={handleRejectionModalShow}
          triggerRequestAccept={triggerRequestAccept}
          travelIntervalTypes={dependencies?.allowed_interval_types}
        />
      ) : (
        <ExpenseRequestControls
          userRole={userRole}
          isReadyToBeAccepted={isReadyToBeAccepted}
          isReadyToBeReturned={isReadyToBeReturned}
          loadingState={loadingState}
          handleCostLineCreation={handleCostLineCreation}
          handleCostLineCreationWithOCR={handleCostLineCreationWithOCR}
          handleWithdrawalModalShow={handleWithdrawalModalShow}
          triggerRequestSave={triggerRequestSave}
          triggerRequestSubmit={triggerRequestSubmit}
          triggerRequestReturn={triggerRequestReturn}
          handleRejectionModalShow={handleRejectionModalShow}
          triggerRequestAccept={triggerRequestAccept}
          currentCostLinesCount={costLines.length}
          requestStatus={requestStatus}
        />
      )}
    </Card>
  );
}

export default memo(RequestControlsContainer);
